export interface WizardQuote {
  /** Empty when the tick carried no price (an option tick can carry only IV). */
  price: string
  changePercent?: number
  /** Options only: mark IV in percent (bid/ask mid when mark is absent). */
  iv?: string
  /** Options only: the contract's underlying price, used to re-pick the ATM strike. */
  underlyingPrice?: string
}

export type WizardPriceMap = Record<string, WizardQuote>

// Caps how many symbols the wizard remembers across its whole lifetime in
// this tab. Without a cap, browsing many different assets in one session
// (or across many dialog open/close cycles) would grow this map forever —
// it's small reference data, not the source of truth, so bounding it and
// evicting the least-recently-updated entries is the right tradeoff over
// either unbounded growth or clearing on every asset switch.
export const WIZARD_PRICE_STORE_MAX_ENTRIES = 500

// Module-level singleton, deliberately outside React: the watchlist wizard
// dialog unmounts its detail pane on close (Radix Dialog doesn't keep
// closed content mounted), which would wipe any price cache held in
// component state. Living at module scope instead means the cache survives
// both switching between assets (BTC -> ETH -> BTC keeps BTC's last known
// prices) and closing/reopening the dialog within the same tab session. A
// full page reload still starts fresh, which is correct — a brand new page
// load already means a brand new WS connection, and there's no stale
// snapshot worth carrying across that boundary.
class WizardPriceStore {
  private map: WizardPriceMap = {}
  private readonly recency: string[] = []
  private readonly listeners = new Set<() => void>()

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  getSnapshot = (): WizardPriceMap => this.map

  merge(updates: WizardPriceMap): void {
    const keys = Object.keys(updates)
    if (keys.length === 0) return

    let changed = false
    const next = { ...this.map }
    for (const key of keys) {
      const prev = next[key]
      // Field-wise: incremental option ticks carry only what changed, so a tick without
      // IV (or price) must not wipe the last known one.
      const quote: WizardQuote = { ...prev, price: prev?.price ?? '' }
      for (const [field, value] of Object.entries(updates[key])) {
        if (value !== undefined && value !== '') Object.assign(quote, { [field]: value })
      }
      this.touch(key)
      if (
        prev?.price === quote.price &&
        prev?.changePercent === quote.changePercent &&
        prev?.iv === quote.iv &&
        prev?.underlyingPrice === quote.underlyingPrice
      ) {
        continue
      }
      next[key] = quote
      changed = true
    }

    while (this.recency.length > WIZARD_PRICE_STORE_MAX_ENTRIES) {
      const evictKey = this.recency.shift()
      if (evictKey !== undefined && evictKey in next) {
        delete next[evictKey]
        changed = true
      }
    }

    if (!changed) return
    this.map = next
    this.listeners.forEach((listener) => listener())
  }

  private touch(key: string): void {
    const index = this.recency.indexOf(key)
    if (index !== -1) this.recency.splice(index, 1)
    this.recency.push(key)
  }
}

export const wizardPriceStore = new WizardPriceStore()
